import React, { useState } from 'react';
import { Box, IconButton, Tooltip, Typography, Chip } from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { JournalEntry, PeriodStatus } from '../types';
import { ColumnDef, ColumnId } from '../hooks/useColumnVisibility';
import { formatDate, getExpiryMeta } from '../utils';

interface JournalTableRowProps {
  row: JournalEntry;
  globalIdx: number;
  visibleDefs: ColumnDef[];
  gridTemplate: string;
  periodStatus: PeriodStatus | null;
  onEdit: (entry: JournalEntry) => void;
  onDelete: (entry: JournalEntry) => void;
}

const JournalTableRow: React.FC<JournalTableRowProps> = ({
  row, globalIdx, visibleDefs, gridTemplate, periodStatus, onEdit, onDelete,
}) => {
  const [hovered, setHovered] = useState(false);

  const canEdit = periodStatus?.canUpdate ?? true;

  // Проверяем 1-часовое окно для удаления
  const ageMs = row.recordDate
    ? Date.now() - new Date(row.recordDate).getTime()
    : Infinity;
  const canDelete = (periodStatus?.canDelete ?? true) && ageMs <= 60 * 60 * 1000;

  const expiryMeta = getExpiryMeta(row.expiryDate);

  const cellStyle = {
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    minWidth: 0, alignSelf: 'center',
  };

  const renderCell = (col: ColumnDef) => {
    switch (col.id as ColumnId) {
      case 'idx':
        return (
          <Typography variant="caption" color="text.disabled" fontWeight={600}>
            {globalIdx}
          </Typography>
        );

      case 'name':
        return (
          <Tooltip title={row.materialName} placement="top">
            <Typography variant="body2" fontWeight={600} noWrap>
              {row.materialName}
            </Typography>
          </Tooltip>
        );

      case 'producer':
        return (
          <Typography variant="caption" color="text.secondary" noWrap>
            {row.producer || '-'}
          </Typography>
        );

      case 'serial':
        return (
          <Typography variant="caption" fontFamily="monospace" noWrap>
            {row.serialNumber || '-'}
          </Typography>
        );

      case 'expiry':
        return (
          <Chip
            size="small"
            label={row.expiryDate ? row.expiryDate.split('T')[0] : '-'}
            sx={{
              bgcolor: expiryMeta.bgcolor,
              color: expiryMeta.color,
              fontWeight: 600,
              fontSize: '0.72rem',
              height: 22,
              borderRadius: 0.75,
            }}
          />
        );

      case 'qty':
        return (
          <Typography variant="body2" fontWeight={700} textAlign="right" noWrap sx={{ width: '100%' }}>
            {row.quantity > 0 ? row.quantity.toLocaleString('ru-RU') : '-'}
          </Typography>
        );

      case 'qtyPcs':
        return (
          <Typography variant="body2" textAlign="right" noWrap sx={{ width: '100%' }}>
            {(row.quantityPcs ?? 0) > 0 ? (row.quantityPcs ?? 0).toLocaleString('ru-RU') : '-'}
          </Typography>
        );

      case 'price':
        return (
          <Typography variant="body2" textAlign="right" noWrap sx={{ width: '100%' }}>
            {row.price > 0 ? row.price.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
          </Typography>
        );

      case 'totalSum':
        return (
          <Typography variant="body2" fontWeight={700} textAlign="right" noWrap sx={{ width: '100%', color: '#16a34a' }}>
            {row.totalSum.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        );

      case 'manager':
        return (
          <Typography variant="caption" color="text.secondary" noWrap>
            {row.managerName || '-'}
          </Typography>
        );

      case 'category':
        return (
          <Typography variant="caption" color="text.secondary" noWrap>
            {row.categoryName || '-'}
          </Typography>
        );

      case 'rxotc':
        return (
          <Typography variant="caption" color="text.secondary" fontWeight={600} noWrap>
            {row.rxotc || '-'}
          </Typography>
        );

      case 'code':
        return (
          <Typography variant="caption" fontFamily="monospace" noWrap>
            {row.nomenclatureCode || '-'}
          </Typography>
        );

      case 'barcode':
        return (
          <Typography variant="caption" color="text.secondary" noWrap>
            {row.barcode || '-'}
          </Typography>
        );

      case 'comment':
        return (
          <Typography variant="caption" color="text.secondary" noWrap>
            {row.comment || '-'}
          </Typography>
        );

      case 'recordDate':
        return (
          <Typography variant="caption" color="text.secondary" noWrap>
            {formatDate(row.recordDate)}
          </Typography>
        );

      case 'actions':
        return (
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
            <Tooltip title={canEdit ? 'Редактировать' : 'Редактирование заблокировано'}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => onEdit(row)}
                  disabled={!canEdit}
                  sx={{ '&:hover': { color: 'primary.main' } }}
                >
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={canDelete ? 'Удалить' : ageMs > 60 * 60 * 1000 ? 'Прошло более 1 часа' : 'Удаление заблокировано'}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => onDelete(row)}
                  disabled={!canDelete}
                  sx={{ '&:hover': { color: 'error.main' } }}
                >
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: 'grid',
        gridTemplateColumns: gridTemplate,
        gap: 1,
        px: 2,
        py: 0.75,
        borderBottom: '1px solid',
        borderColor: 'divider',
        alignItems: 'center',
        bgcolor: hovered ? 'action.hover' : 'transparent',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      {visibleDefs.map(col => (
        <Box key={col.id} sx={cellStyle}>
          {renderCell(col)}
        </Box>
      ))}
    </Box>
  );
};

export default JournalTableRow;
